"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbox } from "@/components/lightbox";
import { Badge } from "@/components/ui/badge";
import { Artwork, pixelsArtworks, pixelsCategories } from "@/lib/data";

export function ArtGallery() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [lightboxArtwork, setLightboxArtwork] = useState<Artwork | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    if (selectedCategory === "All") return pixelsArtworks;
    return pixelsArtworks.filter((a) => a.category === selectedCategory);
  }, [selectedCategory]);

  return (
    <section id="fine-art" className="py-24 px-4 md:px-8">
      <div className="max-w-8xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#c9a84c] text-sm font-semibold tracking-widest uppercase mb-3">
            Fine Art Collection
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            Paintings & Illustrations
          </h2>
          <div className="section-divider mb-6" />
          <p className="text-gray-400 max-w-2xl mx-auto">
            {pixelsArtworks.length} original artworks available as canvas prints, framed prints,
            tapestries, and more on Pixels.com
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {pixelsCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedCategory === cat
                  ? "bg-[#c9a84c] text-black"
                  : "bg-[#1e1e1e] text-gray-400 hover:bg-[#2a2a2a] hover:text-white border border-[#333]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Masonry Gallery */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="gallery-grid"
          >
            {filtered.map((artwork, idx) => (
              <motion.div
                key={artwork.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="gallery-item art-card group relative cursor-pointer"
                onClick={() => setLightboxArtwork(artwork)}
              >
                <div className="relative overflow-hidden rounded-xl bg-[#1a1a1a]">
                  {!imageErrors.has(artwork.id) ? (
                    <Image
                      src={artwork.imageUrl}
                      alt={artwork.title}
                      width={400}
                      height={400}
                      className="w-full h-auto object-cover"
                      unoptimized
                      onError={() =>
                        setImageErrors((prev) => new Set([...prev, artwork.id]))
                      }
                    />
                  ) : (
                    <div className="w-full aspect-square flex items-center justify-center bg-[#1e1e1e] text-gray-600 text-xs p-4 text-center">
                      {artwork.title}
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                    <p className="text-white font-semibold text-sm leading-tight">
                      {artwork.title}
                    </p>
                    <Badge
                      variant="secondary"
                      className="mt-1 self-start bg-[#c9a84c]/20 text-[#c9a84c] border-[#c9a84c]/30 text-xs"
                    >
                      {artwork.category}
                    </Badge>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* View more link */}
        <div className="text-center mt-12">
          <a
            href="https://pixels.com/profiles/priscilla-wolfe"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[#c9a84c] text-[#c9a84c] px-8 py-3 rounded-full hover:bg-[#c9a84c] hover:text-black transition-all duration-200 font-medium"
          >
            View All 533 Artworks on Pixels.com →
          </a>
        </div>
      </div>

      <Lightbox artwork={lightboxArtwork} onClose={() => setLightboxArtwork(null)} />
    </section>
  );
}
